import { Weekday, KioskAdSetFragment } from '@occc/graphql';
import { CalendarGridDayTimeLineItem, CalendarGridTimeString } from '@occc/ui-design-system';

export type ScheduleItemData = {
  adSetId: string;
  adSetName: string;
};

/**
 * Scheduler data structure mapping weekdays to their calendar items.
 * Each weekday contains an array of time-based items to display in the calendar grid.
 */
export type SchedulerData = Partial<
  Record<Weekday, CalendarGridDayTimeLineItem<ScheduleItemData>[]>
>;

/**
 * Converts a time string to the calendar grid format (HH:MM).
 * Ensures hours and minutes are zero-padded to 2 digits.
 */
const formatTimeToCalendarFormat = (time: string): CalendarGridTimeString => {
  const [hours, minutes] = time.split(':');
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}` as CalendarGridTimeString;
};

type MapAdSetsToSchedulerParams = {
  adSets: KioskAdSetFragment[];
  selectedKioskIds: string[];
};

/**
 * Maps ad sets with schedules to a calendar grid data structure.
 */
export const mapAdSetsToScheduler = ({
  adSets,
  selectedKioskIds,
}: MapAdSetsToSchedulerParams): SchedulerData => {
  const scheduler: SchedulerData = {};

  if (!selectedKioskIds.length) {
    return scheduler;
  }

  const kioskIndexMap = new Map(selectedKioskIds.map((id, index) => [id, index]));

  adSets.forEach((adSet) => {
    if (!adSet.schedules || adSet.schedules.length === 0) {
      return;
    }

    const matchingKioskIds = adSet.kioskIds.filter((id) => kioskIndexMap.has(id));

    matchingKioskIds.forEach((kioskId) => {
      const timeLaneIndex = kioskIndexMap.get(kioskId) ?? 0;

      adSet.schedules.forEach((schedule) => {
        if (!schedule.days || schedule.days.length === 0) {
          return;
        }

        schedule.days.forEach((day) => {
          if (!scheduler[day]) {
            scheduler[day] = [];
          }

          scheduler[day]!.push({
            id: `${adSet.id}-${day}-${schedule.startTime}-${kioskId}`,
            startTime: formatTimeToCalendarFormat(schedule.startTime),
            endTime: formatTimeToCalendarFormat(schedule.endTime),
            subFooter: kioskId,
            footer: 'Kiosk ID',
            timeLaneIndex,
            data: {
              adSetId: adSet.id,
              adSetName: adSet.name,
            },
          });
        });
      });
    });
  });

  Object.keys(scheduler).forEach((day) => {
    scheduler[day as Weekday]!.sort((a, b) => a.startTime.localeCompare(b.startTime));
  });

  return scheduler;
};
